import assert from "node:assert";
import { z } from "zod";
import { getEnvBindingsOfType, ProxyNodeBinding } from "../shared";
import type { Worker_Binding } from "../../runtime";
import type { ParsedMiniflareWorkerConfig } from "../shared";
import type { Plugin } from "../shared";

export const HYPERDRIVE_PLUGIN_NAME = "hyperdrive";

/** Resolves `hyperdrive` env bindings, parsing each connection string. */
function getHyperdrives(
	config: ParsedMiniflareWorkerConfig
): [name: string, url: URL][] {
	return getEnvBindingsOfType(config, "hyperdrive").map(([name, binding]) => [
		name,
		HyperdriveSchema.parse(binding.localConnectionString),
	]);
}

function hasPostgresProtocol(url: URL) {
	return url.protocol === "postgresql:" || url.protocol === "postgres:";
}

function hasMysqlProtocol(url: URL) {
	return url.protocol === "mysql:";
}

function getPort(url: URL) {
	if (url.port !== "") return url.port;
	if (hasPostgresProtocol(url)) return "5432";
	if (hasMysqlProtocol(url)) return "3306";
	// Validated in `HyperdriveSchema`
	assert.fail(`Expected known protocol, got ${url.protocol}`);
}

// TODO: upstream this to cloudflare/config
export const HyperdriveSchema = z
	.union([z.url(), z.instanceof(URL)])
	.transform((url, ctx) => {
		if (typeof url === "string") url = new URL(url);
		if (url.protocol === "") {
			ctx.addIssue({
				code: "custom",
				message:
					"You must specify the database protocol - e.g. 'postgresql'/'mysql'.",
			});
		} else if (!hasPostgresProtocol(url) && !hasMysqlProtocol(url)) {
			ctx.addIssue({
				code: "custom",
				message:
					"Only PostgreSQL-compatible or MySQL-compatible databases are currently supported.",
			});
		}
		if (url.host === "") {
			ctx.addIssue({
				code: "custom",
				message:
					"You must provide a hostname or IP address in your connection string - e.g. 'user:password@database-hostname.example.com:5432/databasename",
			});
		}
		if (url.pathname === "") {
			ctx.addIssue({
				code: "custom",
				message:
					"You must provide a database name as the path component - e.g. /postgres",
			});
		}
		if (url.username === "") {
			ctx.addIssue({
				code: "custom",
				message:
					"You must provide a username - e.g. 'user:password@database.example.com:port/databasename'",
			});
		}
		if (url.password === "") {
			ctx.addIssue({
				code: "custom",
				message:
					"You must provide a password - e.g. 'user:password@database.example.com:port/databasename' ",
			});
		}

		return url;
	});

export const HYPERDRIVE_PLUGIN: Plugin = {
	bindingTypeDescription: "Hyperdrive",
	getBindings(options) {
		return getHyperdrives(options.config).map<Worker_Binding>(([name, url]) => {
			const database = url.pathname.replace("/", "");
			const scheme = url.protocol.replace(":", "");
			return {
				name,
				hyperdrive: {
					designator: {
						name: `${HYPERDRIVE_PLUGIN_NAME}:${name}`,
					},
					database: decodeURIComponent(database),
					user: decodeURIComponent(url.username),
					password: decodeURIComponent(url.password),
					scheme,
				},
			};
		});
	},
	getNodeBindings(options) {
		return Object.fromEntries(
			getHyperdrives(options.config).map(([name, url]) => {
				const connectionOverrides: Record<string | symbol, string | number> = {
					connectionString: `${url}`,
					port: Number.parseInt(url.port),
					host: url.hostname,
				};
				const proxyNodeBinding = new ProxyNodeBinding({
					get(target, prop) {
						return prop in connectionOverrides
							? connectionOverrides[prop]
							: target[prop];
					},
				});
				return [name, proxyNodeBinding];
			})
		);
	},
	async getServices({ options, hyperdriveProxyController }) {
		const services = [];
		for (const [name, url] of getHyperdrives(options.config)) {
			const scheme = url.protocol.replace(":", "");
			const sslmode = parseSslMode(url, scheme);
			const targetPort = getPort(url);

			let address: string;
			if (sslmode === "disable") {
				// No SSL requested (either explicitly disabled or not specified):
				// connect directly to the database without a proxy server, avoiding
				// potential issues with local proxy port binding or firewall rules
				address = `${url.hostname}:${targetPort}`;
			} else {
				// SSL modes (require, prefer) need the proxy to handle
				// TLS negotiation with the target database
				const proxyPort = await hyperdriveProxyController.createProxyServer({
					name,
					targetHost: url.hostname,
					targetPort,
					scheme,
					sslmode,
					sslrootcert: parseSslRootCert(url),
				});
				address = `127.0.0.1:${proxyPort}`;
			}

			services.push({
				name: `${HYPERDRIVE_PLUGIN_NAME}:${name}`,
				external: {
					address,
					tcp: {},
				},
			});
		}
		return services;
	},
};

// Postgres sslmode docs: https://www.postgresql.org/docs/current/libpq-ssl.html
// MySQL ssl-mode docs: https://dev.mysql.com/doc/refman/8.4/en/using-encrypted-connections.html
function parseSslMode(url: URL, scheme: string): string {
	// Normalize keys/values to lowercase
	const params = Object.fromEntries(
		Array.from(url.searchParams.entries()).map(([k, v]) => [
			k.toLowerCase(),
			v.toLowerCase(),
		])
	);

	if (scheme === "postgres" || scheme === "postgresql") {
		return params["sslmode"] || "disable"; // disable is default
	} else if (scheme === "mysql") {
		// Parse different variations for mysql sslmode
		const sslmode = params["ssl-mode"] || params["ssl"] || params["sslmode"];

		// Normalize to postgres-equivalent values
		switch (sslmode) {
			case "verify_identity":
				return "verify-full";
			case "verify_ca":
				return "verify-ca";
			case "required":
			case "true":
			case "1":
				return "require";
			case "preferred":
				return "prefer";
			case "disabled":
			case "false":
			case "0":
				return "disable";
		}
	}

	// default to disable
	return "disable";
}

function parseSslRootCert(url: URL): string | undefined {
	const params = Object.fromEntries(
		Array.from(url.searchParams.entries()).map(([k, v]) => [k.toLowerCase(), v])
	);
	return params["sslrootcert"] || undefined;
}

export type {
	HyperdriveProxyController,
	HyperdriveProxyConfig,
	POSTGRES_SSL_REQUEST_PACKET,
} from "./hyperdrive-proxy";
